import React, { useEffect, useState } from 'react';
import {
    Grid,
    Skeleton,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import Chart from 'react-apexcharts';
import axios from 'src/utils/axios';
import { useTheme } from '@mui/material/styles';
import InfoIcon from '@mui/icons-material/InfoOutlined';




const ServiceComplementaryProductAndTurnover = ({ ServiceShopId }) => {
    const [data, setData] = useState([]);
    const [rate, setRate] = useState(0);
    const [totalTurnover, setTotalTurnover] = useState(0);
    const [totalCountOfMarketableComplementaryProduct, setTotalCountOfMarketableComplementaryProduct] = useState(0);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();


    const [today, setToday] = useState(new Date());
    const [firstDay, setFirstDay] = useState(new Date(today.getFullYear(), 0, 1));

    const formatter = new Intl.DateTimeFormat('tr-TR', { day: 'numeric' });
    const formatterMonth = new Intl.DateTimeFormat('tr-TR', { month: 'long' });

    const formatterText = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'numeric', year: 'numeric' });

    const monthNames = {
        Ocak: 0,
        Şubat: 1,
        Mart: 2,
        Nisan: 3,
        Mayıs: 4,
        Haziran: 5,
        Temmuz: 6,
        Ağustos: 7,
        Eylül: 8,
        Ekim: 9,
        Kasım: 10,
        Aralık: 11,
    };

    const getRowMonthEnd = (row) => {
        const [monthName, yearText] = (row.DATESTRING || '').split(' ');
        const monthIndex = monthNames[monthName];
        const year = Number(yearText);

        if (monthIndex === undefined || Number.isNaN(year)) {
            return null;
        }

        return new Date(year, monthIndex + 1, 0);
    };

    const getPreviousMonthEnd = () => new Date(new Date().getFullYear(), new Date().getMonth(), 0);

    useEffect(() => {
        axios.get(`GetComplementaryProductAndTurnover/${ServiceShopId}`).then((response) => {
            if (response) {
                const responseData = response.data;
                const cutoffDate = getPreviousMonthEnd();

                const visibleData = (responseData || [])
                    .map((row) => ({
                        row,
                        monthEnd: getRowMonthEnd(row),
                        turnover: Number(row.TOPLAM_KDVSIZ_ARCELIK_CIRO),
                    }))
                    .filter(({ monthEnd, turnover }) => monthEnd && monthEnd <= cutoffDate && turnover > 0)
                    .sort((left, right) => left.monthEnd - right.monthEnd)
                    .map(({ row }) => row);

                setData(visibleData);
                if (visibleData?.length > 0) {
                    const reportYear = cutoffDate.getFullYear();
                    const tempData = visibleData.filter((item) => item.YEAR == reportYear);
                    const turnOver = tempData.reduce((acc, item) => acc + item.TOPLAM_KDVSIZ_ARCELIK_CIRO, 0);
                    setTotalTurnover(turnOver);

                    const countOfMarketableComplementaryProduct = tempData.reduce((acc, item) => acc + item.TOPLAM_FIS_SAYISI, 0);
                    setTotalCountOfMarketableComplementaryProduct(countOfMarketableComplementaryProduct);

                    const rate = (turnOver / countOfMarketableComplementaryProduct).toFixed(2);
                    setRate(rate);

                    setToday(cutoffDate);
                    setFirstDay(new Date(cutoffDate.getFullYear(), 0, 1));
                }
            }
            setLoading(false);
        });
    }, [ServiceShopId]);

    const MonthlyTurnoverChart = () => {

        const series = [
            {
                name: 'Ciro (₺)',
                data: data
                    ? data.map((item) => {
                        const turnover = Number(item.TOPLAM_KDVSIZ_ARCELIK_CIRO);
                        return turnover > 0 ? turnover : null;
                    })
                    : [],
            },
            {
                name: 'Fiş Başına Tutar (₺)',
                data: data
                    ? data.map(
                        (item) => {
                            const turnover = Number(item.TOPLAM_KDVSIZ_ARCELIK_CIRO);
                            const receiptCount = Number(item.TOPLAM_FIS_SAYISI);

                            if (turnover <= 0 || receiptCount <= 0) {
                                return null;
                            }

                            return Number((turnover / receiptCount).toFixed(2));
                        }
                    )
                    : [],
                type: 'line', // Çizgi grafik olarak gösterilecek
            },
        ];

        const options = {
            chart: {
                type: 'bar',
                toolbar: { show: false },
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '50%',
                    dataLabels: {
                        position: 'top', // Bar üstü veri gösterimi
                    },
                },
            },
            dataLabels: {
                enabled: false,
                formatter: (val, opts) => {
                    //const count = data[opts.dataPointIndex].count;
                    //const rate = (val / count).toFixed(2);
                    return `Fiş Başına Tutar (₺): ${rate}`;
                },
                offsetY: -20,
                style: {
                    fontSize: '12px',
                    colors: ['#304758'],
                },
            },
            stroke: {
                width: [0, 4],
                curve: 'straight',
            },
            xaxis: {
                categories: data ? data.map((item) => item.DATESTRING) : [],
                labels: {
                    style: {
                        fontSize: '12px',
                    },
                },
            },
            yaxis: [
                {
                    title: {
                        text: 'Ciro (₺)',
                    },
                    labels: {
                        formatter: (val) => `₺${val.toLocaleString()}`,
                    },
                    min: 0,
                },
                {
                    opposite: true,
                    title: {
                        text: 'Fiş Başına Tutar (₺)',
                    },
                    labels: {
                        formatter: (val) => `₺${val.toLocaleString()}`,
                    },
                    min: 0,
                }
            ],
            title: {
                text: 'Tamamlayıcı Ürün Ciro ve Fiş Başına Tutar',
            },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                hideEmptySeries: false,
                // y: {
                //     formatter: (val) => {
                //         return `Ciro: ₺${val.toLocaleString()}<br>Oran: ${rate}`;
                //     },
                // },
                custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                    const value = Number(series[seriesIndex][dataPointIndex]);
                    const fisSayisi = data[dataPointIndex].TOPLAM_FIS_SAYISI;
                    const turnOverRate = (value / fisSayisi).toFixed(2);
                    return `
                 <div class="custom-tooltip" style="padding: 10px; font-size: 12px; border: 1px solid #ccc; border-radius: 5px; background: white;">
                     <span class="apexcharts-tooltip-marker" style="background: #F67D00; border-radius: 50%; display: inline-block; width: 10px; height: 10px; margin-right: 5px;"></span>
                     <strong>Ciro:</strong> ₺${Number(value.toFixed(2)).toLocaleString('tr-TR')}<br>
                     <span class="apexcharts-tooltip-marker" style="background: #28A745; border-radius: 50%; display: inline-block; width: 10px; height: 10px; margin-right: 5px;"></span>
                     <strong>Fiş Adedi:</strong> ${fisSayisi.toLocaleString('tr-TR')}<br>
                     <span class="apexcharts-tooltip-marker" style="background: #0539B6; border-radius: 50%; display: inline-block; width: 10px; height: 10px; margin-right: 5px;"></span>
                     <strong>Fiş Başına Tutar:</strong> ₺${Number(turnOverRate).toLocaleString('tr-TR')}
                 </div>`;
                },
            },
            colors: ['#F67D00', '#0539B6', '28A745'],
        };

        return <Chart options={options} series={series} type="bar" height={400} />;
    };

    const TotalRateRadialBarChart = () => {
        const series = [rate]; // rate değerini buraya yazın
        const options = {
            chart: {
                height: 220,
                type: 'radialBar',
                offsetY: -10
            },
            plotOptions: {
                radialBar: {
                    startAngle: -135,
                    endAngle: 135,
                    hollow: {
                        margin: 0,
                        size: '70%',
                        image: undefined,
                        imageOffsetX: 0,
                        imageOffsetY: 0,
                        imageWidth: 150,
                        imageHeight: 150,
                        imageClipped: true,
                        position: 'front'
                    },
                    track: {
                        strokeWidth: '97%',
                        margin: 5, // margin is in pixels
                        dropShadow: {
                            enabled: true,
                            top: 2,
                            left: 0,
                            blur: 4,
                            opacity: 0.15
                        }
                    },
                    dataLabels: {
                        name: {
                            show: true,
                            color: '#F67D00'
                        },
                        value: {
                            show: true,
                            fontSize: '28px',
                            offsetY: 28,
                            formatter: (val) => `${val}` // % işaretini kaldırır
                        }
                    },
                }
            },
            fill: {
                colors: ['#F67D00'] // RadialBar çizgi rengini mavi yapar
            },
            labels: ['Oran'], // RadialBar üzerinde yazacak metin
            title: {
                //text: formatter.format(firstDay) + " - " + formatter.format(today) + " Toplam Ek Garanti Ciro ve Fiş Adedi Oranı",
                text: `${formatter.format(firstDay)} - ${formatter.format(today)} ${formatterMonth.format(today)} ${today.getFullYear()} Toplam Tamamlayıcı Ürün Ciro ve Fiş Adedi Oranı`,
            }
        };

        return <Chart options={options} series={series} type="radialBar" height={220} />;
    };




    return (
        <div>
            <Grid container spacing={3}>
                <Grid size={12}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={150} width={'100%'} />
                    ) : (
                        <>
                            <Grid container spacing={1} alignItems="center" justifyContent="center">
                                <Grid textAlign="center" size={12}>
                                    <span
                                        style={{
                                            WebkitTextSizeAdjust: '100%',
                                            WebkitFontSmoothing: 'antialiased',
                                            '--rc-drag-handle-size': '12px',
                                            '--rc-drag-handle-mobile-size': '24px',
                                            '--rc-drag-handle-bg-colour': 'rgba(0, 0, 0, .2)',
                                            '--rc-drag-bar-size': '6px',
                                            '--rc-border-color': 'rgba(255, 255, 255, .7)',
                                            '--rc-focus-color': '#0088ff',
                                            '--sjs-transition-duration': '150ms',
                                            '--sjs-default-font-family': '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                            lineHeight: '1.334em',
                                            '--sv-modern-mark': 'true',
                                            color: '#263238',
                                            userSelect: 'none',
                                            textAnchor: 'start',
                                            dominantBaseline: 'auto',
                                            fontSize: '14px',
                                            fontWeight: 900,
                                            fill: 'rgb(55, 61, 63)',
                                            boxSizing: 'border-box',
                                            margin: 0,
                                            padding: 0,
                                            fontFamily: 'Helvetica, Arial, sans-serif',
                                            opacity: 1,
                                        }}
                                    >
                                        {`${formatterText.format(firstDay)} - ${formatterText.format(today)}`}
                                    </span>
                                </Grid>
                                <Grid size={12}>
                                    <Grid container justifyContent="center">
                                        <Grid>
                                            <Box
                                                sx={{
                                                    width: 400, // Tablo genişliği
                                                    padding: 2,
                                                }}
                                            >
                                                <TableContainer component={Paper}>
                                                    <Table
                                                        sx={{
                                                            minWidth: 300,
                                                            "& .MuiTableCell-root": {
                                                                padding: "4px 8px", // Hücreler arasındaki boşluğu azalt
                                                            },
                                                        }}
                                                        size="small" // Boşlukları azaltmak için
                                                    >
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell align="center"><b>Toplam Ciro</b></TableCell>
                                                                <TableCell align="center"><b>Toplam Fiş</b></TableCell>
                                                                <TableCell align="center"><b>Fiş Başına Tutar</b></TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>

                                                            <TableRow>
                                                                <TableCell align="center" sx={{ color: "#F67D00" }}>
                                                                    ₺{Number(Number(totalTurnover).toFixed(2)).toLocaleString('tr-TR')}
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ color: "#F67D00" }}>
                                                                    {totalCountOfMarketableComplementaryProduct.toLocaleString('tr-TR')}
                                                                </TableCell>
                                                                <TableCell align="center" sx={{ color: "#F67D00" }}>
                                                                    ₺{Number(Number(rate).toFixed(2)).toLocaleString('tr-TR')}
                                                                </TableCell>
                                                            </TableRow>

                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {/* <TotalRateRadialBarChart />
                            <Box display="flex" flexDirection={'column'} alignItems={'center'}>

                                <Typography variant="h6" gutterBottom textAlign={'left'}>
                                    <b>Toplam Ciro: </b> ₺{Number(Number(totalTurnover).toFixed(2)).toLocaleString('tr-TR')}
                                </Typography>
                                <Typography variant="h6" gutterBottom textAlign={'left'}>
                                    <b>Toplam Fiş: </b>{totalCountOfMarketableComplementaryProduct.toLocaleString('tr-TR')}
                                </Typography>
                            </Box> */}
                        </>

                    )}
                </Grid>
                <Grid size={12}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={350} width={'100%'} />
                    ) : (
                        <MonthlyTurnoverChart />
                    )}
                </Grid>
                <Grid size={12}>
                    {loading ? (
                        <Skeleton variant="rectangular" height={100} width={'100%'} />
                    ) : (
                        <Alert
                            severity="info"
                            variant="outlined"
                            icon={false}
                            sx={{
                                borderColor: theme.palette.info.dark,
                                fontSize: '12px',
                                fontFamily: 'Helvetica, Arial, sans-serif'
                            }}
                        >
                            <List style={{ paddingTop: 3, paddingBottom: 3 }}>
                                <ListItem style={{ paddingTop: 0, paddingBottom: 0 }}>
                                    <ListItemIcon >
                                        <InfoIcon sx={{ color: theme.palette.primary.main, backgroundColor: 'transparent' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        disableTypography
                                        sx={{
                                            fontSize: '12px',
                                            fontFamily: 'Helvetica, Arial, sans-serif'
                                        }}
                                        primary="Yetkili Servisin, Arçelik Pazarlama A.Ş'den satın aldığı ürünlerin KDV hariç toplam cirosunu gösterir."
                                    />
                                </ListItem>
                                <ListItem style={{ paddingTop: 3, paddingBottom: 3 }}>
                                    <ListItemIcon>
                                        <InfoIcon sx={{ color: theme.palette.primary.main, backgroundColor: 'transparent' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        disableTypography
                                        sx={{
                                            fontSize: '12px',
                                            fontFamily: 'Helvetica, Arial, sans-serif'
                                        }}
                                        primary="Toplam fiş: Tamamlayıcı ürün satılabilir fişler"
                                    />
                                </ListItem>
                            </List>
                        </Alert>
                    )}
                </Grid>
            </Grid>
        </div >
    );
};

export default ServiceComplementaryProductAndTurnover;